import React, { useRef, useState } from "react";
import { Upload, Loader, Image as ImageIcon } from "lucide-react";
import ImageGrid from "./ImageGrid";
import ImageQuotaDisplay from "./ImageQuotaDisplay";
import useImageCompression from "../../../hooks/useImageCompression";
import ErrorMessage from "../common/ErrorMessage";

/**
 * Modern high-contrast image upload component with fallback quota & drag-and-drop
 */
const ImageUpload = ({
  images = [],
  onImagesChange,
  imageQuota,
  selectedDate,
}) => {
  const fileInputRef = useRef(null);
  const { compressing, progress, compressMultipleImages } = useImageCompression();
  const [error, setError] = useState(null);

  const activeQuota = imageQuota || {
    used: images.length,
    limit: 10,
    remaining: Math.max(0, 10 - images.length),
  };

  const canUploadMore = activeQuota.remaining > 0;

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setError(null);

    const totalAfterUpload = images.length + files.length;

    if (totalAfterUpload > activeQuota.remaining + images.length) {
      const canAdd = activeQuota.remaining;
      setError(
        `Quota exceeded: You can only upload ${canAdd} more image${canAdd === 1 ? "" : "s"}. ` +
          `You selected ${files.length} image${files.length === 1 ? "" : "s"}.`
      );
      e.target.value = "";
      return;
    }

    const invalidFiles = files.filter((file) => !file.type.startsWith("image/"));
    if (invalidFiles.length > 0) {
      setError("All files must be valid images (JPG, PNG, GIF, WEBP, etc.)");
      e.target.value = "";
      return;
    }

    const oversizedFiles = files.filter((file) => file.size > 10 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      setError("Each image must be smaller than 10MB.");
      e.target.value = "";
      return;
    }

    try {
      const compressedImages = await compressMultipleImages(files, 700);
      const updatedImages = [...images, ...compressedImages];
      onImagesChange(updatedImages);
      e.target.value = "";
    } catch (err) {
      console.error("Error processing images:", err);
      setError("Failed to process images. Please try again.");
    }
  };

  const handleRemoveImage = (index) => {
    const updatedImages = images.filter((_, i) => i !== index);
    onImagesChange(updatedImages);
  };

  return (
    <div className="space-y-6 font-sans">
      {/* Quota Indicator Header */}
      <ImageQuotaDisplay quota={activeQuota} selectedDate={selectedDate} />

      {/* Upload Progress Indicator */}
      {compressing && progress && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 flex items-center gap-3">
          <Loader className="w-5 h-5 text-indigo-600 animate-spin shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-indigo-900">
              Compressing photos...
            </p>
            <p className="text-xs text-indigo-700 mt-0.5 truncate">
              Processing {progress.current} of {progress.total}: {progress.fileName}
            </p>
          </div>
        </div>
      )}

      {/* Upload Dropzone Container */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          disabled={compressing || !canUploadMore}
        />

        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={compressing || !canUploadMore}
          className={`
            w-full flex flex-col items-center justify-center p-8 sm:p-10
            border-2 border-dashed rounded-xl transition-all duration-200 group text-center
            ${
              compressing || !canUploadMore
                ? "border-slate-200 bg-slate-50 cursor-not-allowed text-slate-400"
                : "border-slate-300 bg-slate-50/50 hover:bg-slate-100/60 hover:border-slate-900 cursor-pointer"
            }
          `}
        >
          {compressing ? (
            <>
              <Loader className="w-10 h-10 text-indigo-600 animate-spin mb-3" />
              <p className="text-sm font-bold text-slate-900">
                Optimizing Images...
              </p>
            </>
          ) : !canUploadMore ? (
            <>
              <ImageIcon className="w-10 h-10 text-slate-300 mb-3" />
              <p className="text-sm font-bold text-slate-500">
                Daily Image Quota Reached
              </p>
              <p className="text-xs text-slate-400 mt-1">
                You have reached the maximum limit of {activeQuota.limit} images.
              </p>
            </>
          ) : (
            <>
              <div className="w-12 h-12 rounded-xl bg-slate-900 text-white flex items-center justify-center mb-3 shadow-sm group-hover:scale-105 transition-transform">
                <Upload className="w-6 h-6" />
              </div>
              <p className="text-base font-bold text-slate-900">
                Click to Upload Images
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Attach up to {activeQuota.remaining} more photo{activeQuota.remaining === 1 ? "" : "s"} (Max 10MB each)
              </p>
              <span className="inline-block mt-3 text-[11px] font-semibold text-slate-600 bg-white border border-slate-200 px-3 py-1 rounded-full shadow-xs">
                Supports JPG, PNG, WEBP • Auto-compressed to ~700KB
              </span>
            </>
          )}
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <ErrorMessage
          message={error}
          type="error"
          onDismiss={() => setError(null)}
        />
      )}

      {/* Image Preview Grid */}
      {images.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm space-y-3">
          <h5 className="text-xs font-bold text-slate-500 uppercase tracking-wider">
            Attached Images ({images.length})
          </h5>
          <ImageGrid images={images} onRemove={handleRemoveImage} />
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
